import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getSurfaceBorder, getSurfaceColor, radii } from '../../theme/tokens';
import { ProfileImage } from './ProfileImage';
import { buildProfileUrl, formatFollowerCount, formatNetwork } from './formatters';
import type { UiProfile } from './types';

export const SourceProfile = ({ profile }: { profile: UiProfile }) => {
  const theme = useTheme();
  const followers = formatFollowerCount(profile.followerCount);
  const profileUrl = buildProfileUrl(profile.network, profile.handle);

  return (
    <Box
      sx={{
        backgroundColor: getSurfaceColor(theme, 'raised'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.xl}px`,
        p: { md: 3, xs: 2 },
      }}
    >
      <Stack alignItems={{ sm: 'center', xs: 'flex-start' }} direction={{ sm: 'row', xs: 'column' }} spacing={2}>
        <ProfileImage imageUrl={profile.imageUrl} displayName={profile.displayName} handle={profile.handle} size={72} />

        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={1}>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
              {profile.displayName ?? profile.handle ?? 'Unknown profile'}
            </Typography>
            {profile.verified && (
              <VerifiedIcon sx={{ color: theme.palette.info.main, fontSize: 18 }} />
            )}
            <Chip
              label={formatNetwork(profile.network)}
              size="small"
              sx={{
                backgroundColor: getSurfaceColor(theme, 'subtle'),
                border: `1px solid ${getSurfaceBorder(theme)}`,
                fontSize: 11.5,
                fontWeight: 600,
                height: 22,
              }}
            />
          </Stack>

          {profile.handle && (
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>
              @{profile.handle}
            </Typography>
          )}

          <Stack direction="row" flexWrap="wrap" spacing={2} sx={{ mt: 0.5 }}>
            {followers && (
              <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary }}>
                <strong style={{ color: theme.palette.text.primary }}>{followers}</strong>{' '}
                followers
              </Typography>
            )}
            {profile.accountType && (
              <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary }}>
                {profile.accountType}
              </Typography>
            )}
          </Stack>

          {profile.bio && (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: 13.5,
                mt: 0.75,
                maxWidth: 560,
              }}
            >
              {profile.bio}
            </Typography>
          )}

          {profileUrl && (
            <Link
              href={profileUrl}
              rel="noopener noreferrer"
              target="_blank"
              sx={{ alignItems: 'center', display: 'inline-flex', fontSize: 12.5, fontWeight: 600, gap: 0.4, mt: 0.75 }}
            >
              View profile <OpenInNewIcon sx={{ fontSize: 13 }} />
            </Link>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
